import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'
import { safeFormat } from '@/v2/utils/convert'

export const getAppointmentFormat = appointment => {
  const isValidHour = /^([0-1]\d):([0-5]\d)\s?(?:AM|PM)?$/i.test(
    appointment.startsAtTime
  )
  const startsAt = `${safeFormat(appointment.startsAtDate, 'yyyy-M-dd')} ${
    isValidHour ? appointment.startsAtTime : ''
  }`
  const appointmentInfo = appointment.id ? { id: appointment.id } : {}

  return {
    ...appointmentInfo,
    appointment: {
      doctor_id: appointment.doctorId,
      room_id: appointment.roomId,
      patient_id: appointment.registeredPatient?.id,
      duration:
        appointment.duration !== '' ? parseInt(appointment.duration) : 15,
      starts_at: startsAt,
    },
  }
}

export const getWarnings = data => {
  if (!data) return {}

  // Transforming the response to match the previous structure
  const warnings = {
    ...(data.doctor && data.doctor.length ? { doctorId: data.doctor } : {}),
    ...(data.startsAt && data.startsAt.length
      ? { startsAt: data.startsAt }
      : {}),
    ...(data.room && data.room.length ? { roomId: data.room } : {}),
    ...(data.patient && data.patient.length ? { patientId: data.patient } : {}),
    ...(data.duration && data.duration.length
      ? { duration: data.duration }
      : {}),
    ...(data.appointmentType && data.appointmentType.length
      ? { appointmentTypeId: data.appointmentType }
      : {}),
  }

  return warnings
}

// The backend only reports this as a message on starts_at.
export const isRoomUnavailable = warnings =>
  !!warnings?.startsAt?.some(
    warning =>
      warning?.toLowerCase().includes('room') &&
      warning?.toLowerCase().includes('not available')
  )

export const appointmentWarningsKey = formattedAppointment => [
  'AppointmentWarnings',
  JSON.stringify(formattedAppointment),
]

export const fetchAppointmentWarnings = async formattedAppointment => {
  const isUpdate = !!formattedAppointment.id
  const endpoint = isUpdate
    ? `${baseURL}/v2/rest/appointments/warnings/${formattedAppointment.id}`
    : `${baseURL}/v2/rest/appointments/warnings`

  const res = await fetch(endpoint, {
    method: isUpdate ? 'PUT' : 'POST',
    headers,
    body: JSON.stringify(formattedAppointment),
  })

  if (!res.ok) {
    throw new Error('Failed to check appointment warnings')
  }

  return await res.json()
}

export const useCheckAppointmentWarnings = appointment => {
  const formattedAppointment = getAppointmentFormat(appointment)

  const { data, isLoading, isError } = useQuery({
    queryKey: appointmentWarningsKey(formattedAppointment),
    queryFn: () => fetchAppointmentWarnings(formattedAppointment),
    enabled:
      !!formattedAppointment.appointment.doctor_id ||
      !!formattedAppointment.appointment.room_id ||
      !!formattedAppointment.appointment.patient_id,
  })

  const warnings = getWarnings(data)

  return useMemo(
    () => ({
      warnings,
      loading: isLoading,
      error: isError ? new Error('Failed to check appointment warnings') : null,
    }),
    [warnings, isLoading, isError]
  )
}
